import {DOCUMENT, EventEmitter, inject, Injectable, OnDestroy} from '@angular/core';
import {
    AccountInfo,
    AuthenticationResult,
    BrowserAuthError,
    BrowserAuthErrorCodes,
    EventMessage,
    EventType,
    RedirectRequest,
    SilentRequest
} from '@azure/msal-browser';
import {MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService} from '@azure/msal-angular';
import {firstValueFrom, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {AzureService} from './azure.service';
import {SignInData} from '../../models/msal/sign-in-data.model';
import {TokenSuccessPayload} from '../../models/token/token.success.payload.model';
import {STATIC_CONFIGS} from '../../../configs/static.config';
import {AzureSignInMethodEnum} from '../../enums/azure-sign-in-method.enum';
import {SentryService} from '../sentry/sentry.service';
import {MSAL_CACHE_ENCRYPTION_COOKIE_NAME} from '../../constants/msal-cache-encryption-cookie-name.constant';

@Injectable({
    providedIn: 'root'
})
export class AzureWebService implements AzureService, OnDestroy {
    public readonly onSignIn = new EventEmitter<SignInData>(true);
    public readonly onSilentSignIn = new EventEmitter<SignInData | undefined>(true);
    public readonly onSignInFailure = new EventEmitter<AzureSignInMethodEnum>(true);
    protected readonly window = inject(DOCUMENT).defaultView ?? window;
    private readonly document = inject<Document>(DOCUMENT);
    private readonly isDestroying$ = new Subject<void>();
    private readonly msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
    private readonly msalAuthService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly sentryService = inject(SentryService);
    private msalRedirectHandling: Promise<void> | undefined;

    public setup(): Promise<void> {
        this.msalRedirectHandling = firstValueFrom(this.msalAuthService.handleRedirectObservable(), { defaultValue: null })
            .then(result => {
                this.setMSALCookieExpireState(false);
                this.setActiveAccount(result?.account);
            })
            .catch((error: unknown) => {
                this.removeMSALInteractionStateFromLocalstorage();
                this.sentryService.captureException(error);
            });
        this.msalBroadcastService.msalSubject$.pipe(
                takeUntil(this.isDestroying$)
            )
            .subscribe((msg: EventMessage) => {
                const interactionType = msg.interactionType as unknown as AzureSignInMethodEnum;
                if (msg.eventType == EventType.ACQUIRE_TOKEN_FAILURE) {
                    this.onSignInFailure.emit(interactionType);
                }
                if (msg.eventType === EventType.LOGIN_SUCCESS || (interactionType !== AzureSignInMethodEnum.Silent && msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS)) {
                    const payload: TokenSuccessPayload = msg.payload as TokenSuccessPayload;
                    this.setActiveAccount((msg.payload as AuthenticationResult).account);
                    this.setMSALCookieExpireState(false);
                    this.onSignIn.emit({
                        username: payload.tenantId,
                        password: payload.accessToken
                    });
                }
            });
        return Promise.resolve();
    }

    public async login(): Promise<void> {
        // Try silent login first - this will get rid of the redirection flickering when the user is already signed in on MS
        let hasSilentlySignedIn = false;
        try {
            const result: AuthenticationResult = await this.silentLogin();
            if (result) {
                hasSilentlySignedIn = true;
                this.setMSALCookieExpireState(false);
                this.onSignIn.emit({
                    username: result.tenantId,
                    password: result.accessToken
                });
            }
        } catch {
            //Do not show this error - its not useful at all
        }

        if (!hasSilentlySignedIn) {
            await this.loginWithRedirect(true);
        }
    }

    public logout(): Promise<void> {
        try {
            this.msalAuthService.instance.setActiveAccount(null);
        } catch (error) {
            this.sentryService.captureException(error);
        }
        this.removeMSALEntriesFromLocalstorage();
        return Promise.resolve();
    }

    public async tryToReLogin(): Promise<boolean> {
        try {
            const result: AuthenticationResult = await this.silentLogin();
            if (result) {
                this.onSilentSignIn.emit({
                    username: result.tenantId,
                    password: result.accessToken
                });
                return true;
            }
        } catch (e) {
            console.error(e);
            //Dont show anything - we dont handle azure login errors
            this.onSilentSignIn.emit(undefined);
        }
        return false;
    }

    public ngOnDestroy(): void {
        this.isDestroying$.next();
        this.isDestroying$.complete();
    }

    private async loginWithRedirect(shouldRetryOnStaleInteraction: boolean): Promise<void> {
        try {
            const authRequest = this.msalGuardConfig.authRequest;
            await firstValueFrom(authRequest
                                 ? this.msalAuthService.loginRedirect({ ...authRequest } as RedirectRequest)
                                 : this.msalAuthService.loginRedirect());
        } catch (error) {
            const isStaleInteraction = error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.interactionInProgress;
            if (isStaleInteraction && shouldRetryOnStaleInteraction) {
                this.removeMSALInteractionStateFromLocalstorage();
                await this.loginWithRedirect(false);
                return;
            }

            this.sentryService.captureException(error);
            this.onSignInFailure.emit(AzureSignInMethodEnum.Redirect);
        }
    }

    private async silentLogin(): Promise<AuthenticationResult> {
        await firstValueFrom(this.msalAuthService.initialize());
        if (this.msalRedirectHandling) {
            await this.msalRedirectHandling;
        }

        const account = this.getMsalAccount();
        if (!account) {
            throw new Error('No Entra ID account available - a silent sign in is not possible.');
        }

        const silentRequest: SilentRequest = {
            account,
            scopes: STATIC_CONFIGS.msal.scopes
        };

        return await this.msalAuthService.instance.acquireTokenSilent(silentRequest);
    }

    //Removes local storage entries for entra id - the msal library immediately recreates some of the entries, but without data - this is "correct" behavior
    private removeMSALEntriesFromLocalstorage(): void {
        this.setMSALCookieExpireState(true);
        const regex = /(windows|msal)/;
        Object.keys(this.window.localStorage)
            .filter(localStorageKey => regex.test(localStorageKey))
            .forEach(localStorageKey => this.window.localStorage.removeItem(localStorageKey));
    }

    private removeMSALInteractionStateFromLocalstorage(): void {
        const temporaryKeySuffixes = [
            'interaction.status',
            'request.params',
            'request.origin',
            'request.native',
            'urlHash',
            'code.verifier'
        ];
        Object.keys(this.window.localStorage)
            .filter(localStorageKey => localStorageKey.startsWith('msal.') && temporaryKeySuffixes.some(suffix => localStorageKey.endsWith(suffix)))
            .forEach(localStorageKey => this.window.localStorage.removeItem(localStorageKey));
    }

    private getMsalAccount(): AccountInfo | undefined {
        return this.msalAuthService.instance.getActiveAccount() ?? this.msalAuthService.instance.getAllAccounts()[0];
    }

    private setActiveAccount(account?: AccountInfo | null): void {
        const nextActiveAccount = account ?? this.getMsalAccount();
        if (nextActiveAccount) {
            this.msalAuthService.instance.setActiveAccount(nextActiveAccount);
        }
    }

    private setMSALCookieExpireState(shouldDeleteCookie: boolean = false): void {
        const expireDate = shouldDeleteCookie ? 'Thu, 01 Jan 1970 00:00:00 GMT' : 'Sun, 16 Jul 3567 06:23:41 GMT';
        const cookieNamePrefix = MSAL_CACHE_ENCRYPTION_COOKIE_NAME + '=';
        const cookie = this.document.cookie.split('; ')
            .find(cookiePart => cookiePart.startsWith(cookieNamePrefix));
        if (!cookie) {
            return;
        }

        const value = cookie.slice(cookieNamePrefix.length);
        const crossSiteAttributes = this.window.isSecureContext ? '; Secure; SameSite=None' : '';
        this.document.cookie = cookieNamePrefix + value + '; expires=' + expireDate + '; path=/' + crossSiteAttributes;
    }
}
